import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import MasterLayout from "../../masterLayout/MasterLayout";
import Loader from "../../components/Loader";

const DetalleCliente = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [cliente, setCliente] = useState(null);
  const [preferencias, setPreferencias] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [meseros, setMeseros] = useState([]);
  const [meseroSeleccionado, setMeseroSeleccionado] = useState("");
  const [guardandoMesero, setGuardandoMesero] = useState(false);

  useEffect(() => {
    const obtenerDetalles = async () => {
      try {
        const [detalleResponse, meserosResponse] = await Promise.all([
          fetch("/p_cliente", {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ id }),
          }),
          fetch("/app/rt_meseros", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }),
        ]);

        const data = await detalleResponse.json();
        const meserosData = await meserosResponse.json();

        if (data.status === 1 && data.data && data.data.length >= 3) {
          const clienteData = data.data[0][0] || null;
          setCliente(clienteData);
          setPreferencias(data.data[1] || []);
          setHistorial(data.data[2] || []);
          setMeseroSeleccionado(clienteData?.MESERO_REGISTRO || "");
        } else {
          window.notie.alert({ type: "error", text: "Error al cargar la información del cliente." });
        }

        if (meserosData.status === 1) {
          setMeseros(meserosData.data || []);
        }
      } catch (error) {
        console.error("Error al cargar detalles del cliente:", error);
        window.notie.alert({ type: "error", text: "Error de conexión con el servidor." });
      } finally {
        setIsLoading(false);
      }
    };

    obtenerDetalles();
  }, [id]);

  const guardarMeseroRegistro = async () => {
    if (!meseroSeleccionado) {
      window.notie.alert({ type: "warning", text: "Selecciona un mesero antes de guardar." });
      return;
    }

    try {
      setGuardandoMesero(true);
      const response = await fetch("/p_cliente_mesero", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, mesero: meseroSeleccionado }),
      });
      const data = await response.json();

      if (data.status === 1) {
        setCliente((prev) => (prev ? { ...prev, MESERO_REGISTRO: meseroSeleccionado } : prev));
        window.notie.alert({ type: "success", text: "Mesero actualizado correctamente." });
      } else {
        window.notie.alert({ type: "error", text: data.message || "No se pudo actualizar el mesero." });
      }
    } catch (error) {
      console.error("Error al actualizar mesero del registro:", error);
      window.notie.alert({ type: "error", text: "Error de conexión al guardar el mesero." });
    } finally {
      setGuardandoMesero(false);
    }
  };

  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return "N/A";
    const fecha = new Date(fechaStr);
    return fecha.toLocaleString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC"
    });
  };

  return (
    <MasterLayout>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h6 className="text-lg mb-1">Detalle de Miembro/Socio</h6>
          <p className="text-sm text-secondary-light mb-0">Información de perfil, preferencias e historial</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-outline-primary border-primary-600 text-md px-20 py-10 radius-8 d-flex align-items-center gap-2"
        >
          <Icon icon="ion:arrow-back-outline" className="text-lg" /> Volver
        </button>
      </div>

      {isLoading ? (
        <Loader />
      ) : !cliente ? (
        <div className="card text-center p-40">
          <Icon icon="tabler:user-off" className="text-danger text-5xl mb-16 mx-auto" />
          <h5 className="text-secondary-light">Cliente no encontrado</h5>
          <p className="text-sm text-secondary-light">El cliente solicitado no existe o se encuentra inactivo.</p>
        </div>
      ) : (
        <div className="row gy-4">
          {/* Card: Información General */}
          <div className="col-lg-6 col-sm-12">
            <div className="card h-100 p-24 radius-12 shadow-sm border">
              <div className="card-header border-bottom-0 pb-0 d-flex align-items-center gap-2">
                <Icon icon="solar:user-bold" className="text-primary text-2xl" />
                <h6 className="text-md mb-0 fw-bold">Información General</h6>
              </div>
              <div className="card-body pt-16">
                <div className="row gy-3">
                  <div className="col-12">
                    <span className="text-xs text-secondary-light d-block mb-1">Nombre Completo</span>
                    <span className="text-md fw-semibold text-primary-light">
                      {`${cliente.NOMBRES_DP || ""} ${cliente.APELLIDO_PATERNO_DP || ""} ${cliente.APELLIDO_MATERNO_DP || ""}`.trim()}
                    </span>
                  </div>
                  <div className="col-md-6 col-sm-12">
                    <span className="text-xs text-secondary-light d-block mb-1">Correo Electrónico</span>
                    <span className="text-md fw-semibold text-primary-light">{cliente.CORREO_DP || "Sin asignar"}</span>
                  </div>
                  <div className="col-md-6 col-sm-12">
                    <span className="text-xs text-secondary-light d-block mb-1">Teléfono</span>
                    <span className="text-md fw-semibold text-primary-light">{cliente.TELEFONO_DP || "Sin asignar"}</span>
                  </div>
                  <div className="col-md-6 col-sm-12">
                    <span className="text-xs text-secondary-light d-block mb-1">Fecha de Nacimiento</span>
                    <span className="text-md fw-semibold text-primary-light">{cliente.FECHA_NACIMIENTO_DP || "No proporcionada"}</span>
                  </div>
                  <div className="col-md-6 col-sm-12">
                    <span className="text-xs text-secondary-light d-block mb-1">Tipo de Cuenta</span>
                    <span className="badge bg-info-focus text-info-main text-xs px-12 py-4 radius-8">
                      {cliente.TIPO_SESION}
                    </span>
                  </div>
                  <div className="col-md-6 col-sm-12">
                    <span className="text-xs text-secondary-light d-block mb-1">Fecha de Alta</span>
                    <span className="text-md fw-semibold text-primary-light">{formatearFecha(cliente.FECHA_ALTA_CLI)}</span>
                  </div>
                  <div className="col-md-6 col-sm-12">
                    <span className="text-xs text-secondary-light d-block mb-1">Última Sesión</span>
                    <span className="text-md fw-semibold text-primary-light">{formatearFecha(cliente.ULTIMA_SESION_FECHA_CLI)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card: Canal de Registro y Preferencias */}
          <div className="col-lg-6 col-sm-12">
            <div className="row gy-4 h-100">
              {/* Registro */}
              <div className="col-12">
                <div className="card p-24 radius-12 shadow-sm border">
                  <div className="card-header border-bottom-0 pb-0 d-flex align-items-center gap-2">
                    <Icon icon="solar:clipboard-list-bold" className="text-success text-2xl" />
                    <h6 className="text-md mb-0 fw-bold">Canal de Registro</h6>
                  </div>
                  <div className="card-body pt-16">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-xs text-secondary-light d-block mb-1">¿Por dónde se inscribió?</span>
                        <span className="text-md fw-semibold text-primary-main">
                          {cliente.ORIGEN_REGISTRO || "Aplicación Móvil / Redes"}
                        </span>
                      </div>
                      {cliente.ORIGEN_REGISTRO === "Mesero" && (
                        <div className="text-end" style={{ minWidth: "280px" }}>
                          <span className="text-xs text-secondary-light d-block mb-1">Comentario anterior</span>
                          <span className="text-sm text-primary-light d-block mb-2">
                            {cliente.MESERO_REGISTRO || "Sin comentario previo"}
                          </span>
                          <span className="text-xs text-secondary-light d-block mb-1">Mesero que lo inscribió</span>
                          <div className="d-flex align-items-center gap-2 justify-content-end">
                            <select
                              className="form-select"
                              value={meseroSeleccionado}
                              onChange={(e) => setMeseroSeleccionado(e.target.value)}
                              disabled={guardandoMesero}
                            >
                              <option value="">Selecciona un mesero</option>
                              {meseros.map((mesero) => (
                                <option key={mesero.ID_USR} value={mesero.NOMBRE_COMPLETO}>
                                  {mesero.NOMBRE_COMPLETO}
                                  {mesero.PRIMERA_SUCURSAL ? ` - ${mesero.PRIMERA_SUCURSAL}` : ""}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-success"
                              onClick={guardarMeseroRegistro}
                              disabled={guardandoMesero || !meseroSeleccionado || meseroSeleccionado === (cliente.MESERO_REGISTRO || "")}
                            >
                              {guardandoMesero ? "Guardando..." : "Guardar"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Preferencias */}
              <div className="col-12">
                <div className="card p-24 radius-12 shadow-sm border h-100">
                  <div className="card-header border-bottom-0 pb-0 d-flex align-items-center gap-2">
                    <Icon icon="solar:heart-bold" className="text-danger text-2xl" />
                    <h6 className="text-md mb-0 fw-bold">Preferencias del Cliente</h6>
                  </div>
                  <div className="card-body pt-16">
                    {preferencias.length === 0 ? (
                      <p className="text-sm text-secondary-light">Este cliente no ha seleccionado preferencias en la aplicación.</p>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {preferencias.map((pref) => (
                          <span
                            key={pref.ID_PRE}
                            className="badge bg-danger-focus text-danger-main text-sm px-16 py-8 radius-8 d-flex align-items-center gap-1"
                          >
                            <Icon icon="solar:star-bold" className="text-xs" />
                            {pref.NOMBRE_PRE}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla: Historial de Descuentos y Promociones */}
          <div className="col-12">
            <div className="card p-24 radius-12 shadow-sm border">
              <div className="card-header border-bottom-0 pb-0 d-flex align-items-center gap-2">
                <Icon icon="solar:ticket-bold" className="text-warning text-2xl" />
                <h6 className="text-md mb-0 fw-bold">Historial de Descuentos y Promociones Aplicadas</h6>
              </div>
              <div className="card-body pt-24">
                {historial.length === 0 ? (
                  <div className="text-center p-32">
                    <Icon icon="solar:ticket-broken" className="text-secondary-light text-4xl mb-12" />
                    <p className="text-sm text-secondary-light mb-0">
                      No se han aplicado promociones o cupones a este cliente a través de la app.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table bordered-table mb-0 align-middle">
                      <thead>
                        <tr>
                          <th scope="col">Promoción/Cupón</th>
                          <th scope="col">Descuento</th>
                          <th scope="col">Descripción</th>
                          <th scope="col">Fecha de Aplicación</th>
                          <th scope="col">Mesero que Aplicó</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historial.map((item) => (
                          <tr key={item.ID_CUR}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span className="w-32-px h-32-px rounded-circle bg-warning-focus text-warning-main d-flex align-items-center justify-content-center">
                                  <Icon icon="solar:ticket-sale-bold" />
                                </span>
                                <span className="text-md fw-semibold text-primary-light">
                                  {item.TITULO_CUP}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-success-focus text-success-main text-md px-16 py-8 radius-8 fw-bold">
                                {item.VALOR_CUP}
                              </span>
                            </td>
                            <td>
                              <span className="text-sm text-secondary-light">{item.DESCRIPCION_CUP || "Sin descripción"}</span>
                            </td>
                            <td>{formatearFecha(item.FECHA_REDENCION)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span className="w-24-px h-24-px rounded-circle bg-primary-focus text-primary-main d-flex align-items-center justify-content-center text-xs">
                                  <Icon icon="solar:user-bold" />
                                </span>
                                <span className="text-sm fw-semibold text-primary-light">
                                  {item.NOMBRE_MESERO || "Mesero del Sistema"}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default DetalleCliente;
